import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaUndo, FaSave, FaEdit } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/backendConfig';
import { enqueueAndPoll } from '../utils/jobPolling';
import { useAuth } from '../context/AuthContext';

export default function CharacterEditModal({
  isOpen,
  onClose,
  character,
  onCharacterUpdate
}) {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [editHistory, setEditHistory] = useState([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(-1);
  const [characterData, setCharacterData] = useState(null);
  const characterRef = useRef();
  characterRef.current = character;
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen) return;
    const c = characterRef.current;
    if (c) {
      setCharacterData(c);
      const imageUrl = c.imageUrl || c.characterImage;
      setOriginalImageUrl(imageUrl || '');
      setCurrentImageUrl(imageUrl || '');
    }
    setPrompt('');
    setEditHistory([]);
    setCurrentEditIndex(-1);
  }, [isOpen]);

  const handleEditCharacter = async () => {
    if (!prompt.trim()) {
      alert('Please enter a prompt describing the changes you want to make to the character');
      return;
    }

    setIsLoading(true);
    const editPromptText = prompt.trim();
    try {
      // Same approach as SceneEditModal: enqueueAndPoll with config endpoint and job-status polling
      console.log('CharacterEditModal: submitting edit job with prompt:', editPromptText);
      const data = await enqueueAndPoll(
        API_ENDPOINTS.generateCharacterImage,
        {
          characterData: {
            ...characterData,
            userId: user?.uid || characterData?.userId,
          },
          prompt: editPromptText,
          storyId: null,
          imageUrl: fullImageUrl || currentImageUrl || characterData?.imageUrl, // pass current image so BFL edits it
        },
        {
          interval: 2000,
          maxAttempts: 150,
          onProgress: (status, job, attempts) => {
            console.log(`CharacterEditModal - Polling attempt ${attempts}:`, status?.status);
          },
          onStatusChange: (status) => {
            console.log('CharacterEditModal - Job status changed:', status?.status);
          },
          baseUrl: API_ENDPOINTS.backendBase,
        }
      );

      // Extract image URL (same pattern as SceneEditModal: result may be status.result or direct fields)
      let newImageUrl = data?.imageUrl || data?.generatedImageUrl || data?.result?.imageUrl;
      if (!newImageUrl) {
        console.error('CharacterEditModal: no imageUrl in edit result', data);
        throw new Error('Edit completed but no image was returned.');
      }
      if (newImageUrl.startsWith('/uploads/')) {
        newImageUrl = `${API_ENDPOINTS.backendBase}${newImageUrl}`;
      }
      console.log('CharacterEditModal: edit job completed, new image:', newImageUrl);

      const newEdit = {
        imageUrl: newImageUrl,
        prompt: editPromptText,
        timestamp: new Date().toISOString(),
      };

      setEditHistory((prev) => [...prev, newEdit]);
      setCurrentEditIndex((prev) => prev + 1);
      setCurrentImageUrl(newImageUrl);
      setPrompt('');
      // Update parent immediately so Selected Character card shows new image without refresh
      const updatedCharacter = { ...characterData, imageUrl: newImageUrl };
      if (onCharacterUpdate) onCharacterUpdate(updatedCharacter);
    } catch (error) {
      console.error('Error editing character image:', error);
      alert(error?.message || 'Failed to edit character image. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUndo = () => {
    if (currentEditIndex > 0) {
      const newIndex = currentEditIndex - 1;
      setCurrentEditIndex(newIndex);
      setCurrentImageUrl(editHistory[newIndex].imageUrl);
    } else if (currentEditIndex === 0) {
      setCurrentEditIndex(-1);
      setCurrentImageUrl(originalImageUrl);
    }
  };

  const handleRedo = () => {
    if (currentEditIndex < editHistory.length - 1) {
      const newIndex = currentEditIndex + 1;
      setCurrentEditIndex(newIndex);
      setCurrentImageUrl(editHistory[newIndex].imageUrl);
    }
  };

  const handleSave = async () => {
    try {
      const updatedCharacter = { ...characterData, imageUrl: currentImageUrl };
      const hasCharacterId = characterData?.characterId || characterData?.id;

      if (hasCharacterId) {
        const response = await fetch(`${API_ENDPOINTS.backendBase}/api/characters/${characterData.characterId || characterData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl: currentImageUrl }),
        });
        if (!response.ok) throw new Error('Failed to update character');
      }

      if (onCharacterUpdate) onCharacterUpdate(updatedCharacter);
      onClose();
    } catch (error) {
      console.error('Error saving character:', error);
      alert('Failed to save character changes. Please try again.');
    }
  };

  const handleCancel = () => {
    setCurrentImageUrl(originalImageUrl);
    setPrompt('');
    setEditHistory([]);
    setCurrentEditIndex(-1);
    onClose();
  };

  const fullImageUrl = currentImageUrl && currentImageUrl.startsWith('/uploads/')
    ? `${API_ENDPOINTS.backendBase}${currentImageUrl}`
    : currentImageUrl || '';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      {/* Artmosphere loader overlay — full-screen while edit job is running */}
      {isLoading && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Awesome!</h3>
            <p className="text-gray-600">Give a moment — let Artmosphere edit your character.</p>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col relative">
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <FaEdit /> Edit Character
          </h2>
          <button onClick={handleCancel} className="p-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100" aria-label="Close">
            <FaTimes className="text-lg" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex-1 p-4 overflow-auto">
            <div className="w-full h-full flex flex-col items-center justify-center min-h-[40vh]">
              <div className="relative">
                {fullImageUrl ? (
                  <img
                    src={fullImageUrl}
                    alt="Character"
                    className="max-w-full max-h-[50vh] object-contain rounded-xl border border-gray-200 shadow-lg"
                  />
                ) : (
                  <div className="max-w-full w-64 h-64 rounded-xl border border-gray-200 bg-gray-100 flex items-center justify-center text-gray-500">No image</div>
                )}
                
                {/* Character Info Overlay */}
                {characterData && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-2 rounded-b-lg">
                    <h3 className="font-bold text-sm">{characterData.name}</h3>
                    <p className="text-xs opacity-90">
                      {characterData.age} year old {characterData.gender}
                      {characterData.role && ` • ${characterData.role}`}
                    </p>
                  </div>
                )}
              </div>
              
              {editHistory.length > 0 && (
                <div className="mt-3 text-center">
                  <button onClick={handleUndo} disabled={currentEditIndex <= -1} className="bg-slate-600 hover:bg-slate-700 disabled:bg-gray-300 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 mx-auto" title="Undo to previous image">
                    <FaUndo className="text-xs" /> Undo
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="w-80 flex-shrink-0 bg-gray-50 p-4 flex flex-col border-l border-gray-200">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Make your changes</h3>
            <p className="text-xs text-gray-600 mb-3">Describe what you want to change about the character.</p>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Change expression to be more serious, make hair longer, add glasses..."
              className="w-full h-32 p-3 border border-gray-200 rounded-xl focus:border-slate-400 focus:ring-1 focus:ring-slate-200 focus:outline-none resize-none text-sm"
              disabled={isLoading}
            />
            <div className="space-y-3 mt-4">
              <button
                onClick={handleEditCharacter}
                disabled={isLoading || !prompt.trim()}
                className="w-full bg-slate-600 hover:bg-slate-700 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-xl transition flex items-center justify-center gap-2 text-sm"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                    <span>Editing...</span>
                  </>
                ) : (
                  <>
                    <FaEdit />
                    <span>Apply Edit</span>
                  </>
                )}
              </button>
              <div className="flex gap-2">
                <button onClick={handleSave} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2 px-3 rounded-xl flex items-center justify-center gap-1 text-sm">
                  <FaSave /> Save
                </button>
                <button onClick={handleCancel} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-3 rounded-xl text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 